<template>
    <ListActionDialog
        :open="modal.isOpen"
        :type="modal.type"
        :list="modal.list"
        :is-submitting="modal.isSubmitting"
        @close="onListModalClose"
        @submit="onListModalSubmit"
    />

    <SidebarLayout>
        <template #sidebar>
            <Tooltip
                :show-tooltip="
                    getLimitation('lists.total') !== null &&
                    $filters.lodash.size(lists.user) >= getLimitation('lists.total')
                "
            >
                <template #trigger>
                    <button
                        class="btn w-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                        :disabled="
                            getLimitation('lists.total') !== null &&
                            $filters.lodash.size(lists.user) >= getLimitation('lists.total')
                        "
                        @click="openListModal('create')"
                    >
                        <AppIcon
                            name="plus"
                            class="h-[18px] w-[18px] text-slate-100"
                            stroke-width="2.5"
                        />
                        <span class="ml-2 lg:block">
                            Create<span class="hidden xl:inline"> List</span>
                        </span>
                    </button>
                </template>
                <template #default>
                    <div class="whitespace-nowrap text-xs">
                        {{
                            getFromDictionary(`usage.lists.total`).replace(
                                '[LIMIT]',
                                getLimitation('lists.total'),
                            )
                        }}
                    </div>
                </template>
            </Tooltip>

            <input
                v-model="lists.filter.search"
                class="form-input w-full mt-4 lg:mt-2"
                type="text"
                placeholder="Search for a list"
                autocomplete="off"
            />

            <div class="mt-4 lg:mt-2 inline-flex w-full rounded bg-slate-200 p-1">
                <button
                    class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                    :class="{
                        'bg-white text-foreground': state.listType === 'user',
                    }"
                    @click="setListType('user')"
                >
                    <span class="mx-auto text-sm">My Lists</span>
                </button>
                <button
                    class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                    :class="{
                        'bg-white text-foreground': state.listType === 'shared',
                    }"
                    @click="setListType('shared')"
                >
                    <span class="mx-auto text-sm">Shared</span>
                </button>
                <button
                    class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                    :class="{
                        'bg-white text-foreground' : state.listType === 'public',
                    }"
                    @click="setListType('public')"
                >
                    <span class="mx-auto text-sm">Public</span>
                </button>
            </div>

            <div
                ref="scrollContainer"
                class="scrollbar-default mt-4 lg:mt-2 overflow-y-auto overflow-x-hidden max-h-80 lg:max-h-[calc(100vh_-_var(--legacy-banner-height,0px)_-_270px)]"
                :class="{ 'pr-1.5': hasScrollbar }"
            >
                <div class="w-full">
                    <SkeletonUserLists v-if="!state.lists.isLoaded" />

                    <div v-else>
                        <div v-if="$filters.lodash.size(lists[state.listType])" class="space-y-2">
                            <div
                                v-for="(list, index) in filteredLists[state.listType]"
                                :key="`${list.name}-${index}`"
                                @click="onListSelect(list)"
                            >
                                <div
                                    class="cursor-pointer rounded border border-slate-200 bg-slate-50 px-4 py-4 transition duration-200 box-border"
                                    :class="currentList?.id === list.id
                                        ? 'rounded !border-indigo-200 bg-white'
                                        : 'hover:border-indigo-300 hover:bg-white'"
                                >
                                    <div class="flex items-start gap-3">
                                        <div class="min-w-0 flex-1">
                                            <span class="block truncate font-semibold">{{ list.name }}</span>
                                            <span class="block text-sm">{{ list?.totals?.total ?? 0 }} profile{{ (list?.totals?.total ?? 0) === 1 ? '' : 's' }}</span>
                                            <span class="text-xs">Updated {{ $moment(list.updated_at).fromNow() }}</span>
                                        </div>

                                        <div class="shrink-0" @click.stop>
                                            <ActionMenu title="List actions">
                                                <DropdownMenuItem
                                                    v-if="list.isOperatorList === true"
                                                    class="cursor-pointer gap-2"
                                                    @click="openListModal('update', list)"
                                                >
                                                    <Pencil class="h-4 w-4" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    class="cursor-pointer gap-2"
                                                    @click="openListModal('copy', list)"
                                                >
                                                    <Copy class="h-4 w-4" />
                                                    Copy
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator v-if="list.isOperatorList === true" />
                                                <DropdownMenuItem
                                                    v-if="list.isOperatorList === true"
                                                    class="cursor-pointer gap-2 text-red-600 focus:text-red-600"
                                                    @click="openListModal('delete', list)"
                                                >
                                                    <Trash2 class="h-4 w-4" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </ActionMenu>
                                        </div>
                                    </div>

                                    <div
                                        v-if="list.platforms?.length > 0"
                                        class="mt-4 flex flex-col divide-y divide-slate-200 text-xs"
                                    >
                                        <div
                                            v-for="{ key, total } in list.platforms"
                                            :key="key"
                                            class="flex bg-slate-100 px-2.5 py-1.5 first:rounded-tl first:rounded-tr last:rounded-bl last:rounded-br"
                                        >
                                            <AppIcon
                                                :name="key === 'twitter' ? 'x-twitter' : key"
                                                class="my-auto h-3.5 w-3.5"
                                                :class="key === 'twitter' ? 'text-x' : `text-${key}`"
                                            />
                                            <div class="text-semibold my-auto ml-1.5 capitalize leading-none">
                                                {{ key === 'twitter' ? 'X' : key }}
                                            </div>
                                            <div class="ml-auto">{{ total }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="lists.filter.search && !filteredLists[state.listType]?.length"
                                class="overflow-hidden break-words py-2 text-center text-sm"
                            >
                                None of your lists match "{{ lists.filter.search }}"
                            </div>
                        </div>

                        <div v-if="!lists[state.listType] || $filters.lodash.size(lists[state.listType]) == 0">
                            <div class="overflow-hidden break-words py-2 text-center text-sm">No lists found</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #main>
            <template
                v-if="
                    state.lists.isLoaded &&
                    (!lists.user || $filters.lodash.size(lists.user) == 0) &&
                    (!lists.shared || $filters.lodash.size(lists.shared) == 0) &&
                    state.listType !== 'public'
                "
            >
                <div class="flex rounded-sm border border-slate-200 bg-white px-3 py-3">
                    <h5 class="text-left text-lg text-sm font-medium leading-5 text-slate-500">
                        No lists have been created yet, please create one using the "Create list" option.
                    </h5>
                </div>
            </template>
            <template v-else>
                <ListsCreatorTable
                    :creators="creators"
                    :current-list="currentList"
                    :potential-shares="potentialShares"
                    :default-platform-id="state.platformId"
                    :supercampaigns="supercampaigns"
                    :is-editable="state.listType !== 'public'"
                    @add-creators="onAddCreators"
                    @edit-creators="onCreatorsEdit"
                    @on-list-share="onListShareClick"
                    @bulk-import-creators="onBulkImportCreators"
                    @fetch-shared-to="onFetchSharedTo"
                />
            </template>
        </template>
    </SidebarLayout>
</template>

<script>
import _ from 'lodash'
import {mapActions, mapGetters} from 'vuex'
import {SidebarLayout} from '@/components/ui/layouts'
import { ActionMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { Copy, Pencil, Trash2 } from 'lucide-vue-next'
import SkeletonUserLists from '@/partials/skeletons/SkeletonUserLists.vue'
import ListsCreatorTable from '@/partials/tables/creator-lists/ListsCreatorTable.vue'
import ListActionDialog from '@/partials/modals/ListActionDialog.vue'
import Tooltip from '@/components/Tooltip.vue'
import {useBackgroundActions} from '@/composables/useBackgroundActions'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'

export default {
    name: 'ListsPage',

    metaInfo: {
        title: 'Lists',
    },

    components: {
        ActionMenu,
        Copy,
        DropdownMenuItem,
        DropdownMenuSeparator,
        ListActionDialog,
        ListsCreatorTable,
        Pencil,
        SkeletonUserLists,
        Trash2,
        Tooltip,
        SidebarLayout,
    },

    setup() {
        const { backgroundActions, addBackgroundAction, updateBackgroundAction, completeBackgroundAction } =
            useBackgroundActions()

        return {
            backgroundActions,
            addBackgroundAction,
            updateBackgroundAction,
            completeBackgroundAction,
        }
    },

    data() {
        return {
            currentList: {},
            selectedListId: null,
            creators: [],
            newCreators: [],
            supercampaigns: [],

            state: {
                listType: 'user',
                lists: {
                    isLoaded: false,
                },
                creators: {
                    isLoaded: false,
                },
                platformId: 'instagram',
            },

            lists: {
                user: [],
                shared: [],
                filter: {
                    type: 'user',
                    search: '',
                },
            },

            potentialShares: {
                operators: [],
                organizations: [],
            },

            modal: {
                isOpen: false,
                type: 'update',
                list: null,
                isSubmitting: false,
            },
            hasScrollbar: false,
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'selfOperator', 'getLimitation', 'backgroundActions']),

        filteredLists() {
            const hasSearch = this?.lists?.filter?.search
            const searchKeyword = this.lists.filter.search.toLowerCase().trim()

            return _.mapValues(this.lists, (list) => {
                const filtered = _.filter(list, (item) => {
                    if (!hasSearch) return true
                    if (!item?.name) return false
                    return item.name.toLowerCase().includes(searchKeyword)
                })

                return _.orderBy(filtered, ['updated_at'], ['desc'])
            })
        },

        orgDefaultPlatformId() {
            return this.$store.getters.selfDefaultPlatformId
        }
    },

    watch: {
        '$route.query.list_id': {
            immediate: true,
            handler(newVal) {
                if (!newVal) {
                    // User navigated from /lists?list_id= to /lists
                    // Reset key parts of state
                    this.selectedListId = null
                    this.currentList = {}
                    this.creators = []
                    this.state.platformId = this.orgDefaultPlatformId
                    this.state.listType = 'user'
                    this.lists.filter.search = ''

                    // Re-run initialization list logic
                    this.initializeListPage()
                }
            },
        },
    },

    mounted() {
        this.checkScrollbar()

        this.resizeObserver = new ResizeObserver(() => {
            this.checkScrollbar()
        })

        this.resizeObserver.observe(this.$refs.scrollContainer)
    },

    beforeUnmount() {
        this.resizeObserver.disconnect()
    },

    created() {
       this.initializeListPage()

       if (this.orgDefaultPlatformId) {
           this.state.platformId = this.orgDefaultPlatformId
       }
    },

    methods: {
        ...mapActions(['addBackgroundAction', 'updateBackgroundAction', 'completeBackgroundAction']),

        initializeListPage() {
            this.getLists().then(() => {
                if (this.$route.query.list_id) {
                    const activeListId = this.$route.query.list_id

                    const allLists = [...this.lists.user, ...this.lists.shared]

                    const activeList = allLists.find((list) => list.id === activeListId)

                    if (activeList) {
                        this.onListSelect(activeList)
                        return
                    }
                }

                const firstList = _.first(_.orderBy(this.lists.user, ['updated_at'], ['desc']))
                if (firstList) this.onListSelect(firstList)
            })

            this.getSupercampaigns()
            this.getOrganizations()
        },

        setListType(type) {
            this.state.listType = type

            const firstList = _.first(_.orderBy(this.lists[this.state.listType], ['updated_at'], ['desc']))
            if (firstList) {
                this.onListSelect(firstList)
            }
        },

        setActivePlatform(activeList) {

            // This is how we tell if "all" is selected, if it is, don't reassign
            if (this.state.platformId === null) return

            const biggestPlatform = _(activeList.totals)
                .map((value, key) => ({ key, value }))
                .filter((item) => item.key !== 'total')
                .maxBy('value')

            this.state.platformId = biggestPlatform?.key ?? this.orgDefaultPlatformId
        },

        getLists() {
            return this.$axios
                .get('/lists')
                .then(({ data }) => {
                    _.forEach(data.payload.myLists, (list) => {
                        // eslint-disable-next-line no-param-reassign
                        list.type = 'user'
                    })
                    _.forEach(data.payload.sharedLists, (list) => {
                        // eslint-disable-next-line no-param-reassign
                        list.type = 'shared'
                    })

                    this.lists.user = data.payload.myLists
                    this.lists.shared = data.payload.sharedLists
                    this.lists.public = data.payload.publicLists

                    const allLists = [...this.lists.user, ...this.lists.shared, ...this.lists.public]

                    _.forEach(allLists, (list) => {
                        if (this.selectedListId && this.selectedListId === list.id) {
                            this.currentList = list
                        }

                        list.platforms = this.getListPlatforms(list)
                    })

                    this.state.lists.isLoaded = true
                })
                .catch((error) => {
                    this.$errorHandler(error, {
                        title: 'Lists',
                        message: 'Error fetching lists.',
                    })
                })
        },

        openListModal(type, list) {
            this.modal.isOpen = true
            this.modal.type = type
            this.modal.list = list
        },

        onListModalClose() {
            this.modal.isOpen = false
            this.modal.list = null
            this.modal.isSubmitting = false
        },

        getListPlatforms(list) {
            return _.reduce(
                list?.totals || {},
                (result, total, key) => {
                    if (key !== 'total' && total > 0) result.push({ key, total })
                    return result
                },
                [],
            )
        },

        prepareOwnedList(list) {
            const ownedList = {
                ...list,
                totals: list?.totals || {
                    total: 0,
                },
                creators: list?.creators || [],
                type: 'user',
                isOperatorList: true,
            }

            ownedList.platforms = this.getListPlatforms(ownedList)

            return ownedList
        },

        onListSelect(list) {
            if (this.selectedListId === list.id) return

            this.setActivePlatform(list)
            this.selectedListId = list.id
            this.currentList = list

            this.$router.push({
                query: {
                    ...this.$route.query,
                    list_id: _.get(list, 'id'),
                },
            })
        },

        isShared(list, { operatorId, organizationId }) {
            if (list) {
                if (operatorId && list.operators && list.operators.length) {
                    return !!_.find(list.operators, (operator) => operator.id === operatorId)
                }

                if (organizationId && list.organizations && list.organizations.length) {
                    return !!_.find(list.organizations, (organization) => organization.id === organizationId)
                }
            }

            return false
        },

        onListShareClick({ list, type, sharedOrganization, sharedOperator }) {
            if (type === 'add') {
                this.$axios
                    .post(`/lists/${list.id}/share`, {
                        organization_id: sharedOrganization?.id,
                        operator_id: sharedOperator?.id,
                    })
                    .then(() => {
                        if (!list.sharedTo) {
                            list.sharedTo = {
                                operators: [],
                                organizations: [],
                            }
                        }

                        if (sharedOrganization) {
                            if (!_.includes(list.sharedTo.organizations, sharedOrganization.id)) {
                                list.sharedTo.organizations.push(sharedOrganization.id)
                            }
                            this.$successHandler(`Successfully shared list with ${sharedOrganization.name}`)
                        }

                        if (sharedOperator) {
                            if (!_.includes(list.sharedTo.operators, sharedOperator.id)) {
                                list.sharedTo.operators.push(sharedOperator.id)
                            }
                            this.$successHandler(`Successfully shared list with ${sharedOperator.name}`)
                        }
                    })
                    .catch((error) => {
                        this.$errorHandler(error, {
                            title: 'List Access',
                            message: 'Error while updating list access.',
                        })
                    })
            }

            if (type === 'remove') {
                this.$axios
                    .delete(`/lists/${list.id}/share`, {
                        data: {
                            organization_id: sharedOrganization?.id,
                            operator_id: sharedOperator?.id,
                        },
                    })
                    .then(() => {
                        if (list.sharedTo) {
                            if (sharedOrganization) {
                                if (_.includes(list.sharedTo.organizations, sharedOrganization.id)) {
                                    const organizationIndex = _.findIndex(
                                        list.sharedTo.organizations,
                                        (organization) => organization === sharedOrganization.id,
                                    )
                                    list.sharedTo.organizations.splice(organizationIndex, 1)
                                }
                                this.$successHandler('Successfully removed from list.')
                            }

                            if (sharedOperator) {
                                if (_.includes(list.sharedTo.operators, sharedOperator.id)) {
                                    const operatorIndex = _.findIndex(
                                        list.sharedTo.operators,
                                        (operator) => operator === sharedOperator.id,
                                    )
                                    list.sharedTo.operators.splice(operatorIndex, 1)
                                }
                                this.$successHandler('Successfully removed from list.')
                            }
                        }
                    })
                    .catch((error) => {
                        this.$errorHandler(error, {
                            title: 'List Access',
                            message: 'Error while updating list access.',
                        })
                    })
            }
        },

        getSupercampaigns() {
            this.$axios
                .get('/supercampaigns', {
                    params: {
                        relations: ['games', 'campaigns'],
                    },
                })
                .then(({ data }) => {
                    this.supercampaigns = _(data.payload.supercampaigns).orderBy(['created_at'], ['desc']).value()
                })
                .catch((error) => {
                    this.$errorHandler(error, {
                        title: 'Supercampaigns',
                        message: 'Error fetching supercampaigns.',
                    })
                })
        },

        getOrganizations() {
            this.$axios
                .get('/organizations', {
                    params: {
                        relations: ['operators'],
                    },
                })
                .then(({ data }) => {
                    this.potentialShares.organizations = data.payload.organizations

                    const organizationsById = _.keyBy(data.payload.organizations, 'id')

                    const destructuredOperators = _.reduce(
                        data.payload.organizations,
                        (result, organization) => {
                            _.forEach(organization.operators, (operator) => {
                                // This will remove the current operator from the result set
                                if (operator.id === this.selfOperator.id) return

                                if (result[operator.id]) {
                                    result[operator.id].push(operator)
                                } else {
                                    // eslint-disable-next-line no-param-reassign
                                    result[operator.id] = [operator]
                                }
                            })

                            return result
                        },
                        {},
                    )

                    this.potentialShares.operators = _.map(destructuredOperators, (operatorEntries) => {
                        // This is creating a new object because _.omit
                        const firstOperator = _.omit(_.first(operatorEntries), ['organization_operator'])

                        return _.assign(firstOperator, {
                            organizations: _.map(operatorEntries, (entry) => {
                                const organization = _.omit(
                                    organizationsById[entry.organization_operator.organization_id],
                                    ['operators'],
                                )

                                return _.assign(organization, {
                                    role: entry.organization_operator.role,
                                })
                            }),
                        })
                    })
                })
                .catch((error) => {
                    this.$errorHandler(error, {
                        title: 'Organizations',
                        message: 'Error fetching organizations.',
                    })
                })
        },

        async onListModalSubmit({ type, list, name }) {
            const currentList = list || this.modal.list
            const trimmedName = name?.trim() || ''
            const requiresName = ['create', 'update', 'copy'].includes(type)

            if (requiresName && !trimmedName) return
            if (type !== 'create' && !currentList) return

            this.modal.isSubmitting = true

            if (type === 'create') {
                trackEvent(AnalyticalEvents.ListCreated, {
                    list_name: trimmedName,
                })

                try {
                    const { data } = await this.$axios.post('/lists', {
                        list: {
                            name: trimmedName,
                            operator_id: this.selfOperator.id,
                        },
                    })
                    const newList = this.prepareOwnedList(data.payload.list)

                    this.lists.user.push(newList)
                    this.state.listType = 'user'
                    this.onListSelect(newList)
                    this.onListModalClose()
                    this.$successHandler('Successfully created list.')
                } catch (error) {
                    this.$errorHandler(error, {
                        title: 'Creating List',
                        message: 'Error creating new list.',
                    })
                } finally {
                    this.modal.isSubmitting = false
                }

                return
            }

            if (type === 'update') {
                try {
                    const { data } = await this.$axios.put(`/lists/${currentList.id}`, {
                        list: {
                            name: trimmedName,
                        },
                    })
                    const oldList = _.find(this.lists.user, (item) => item.id === currentList.id) || currentList

                    _.assign(oldList, data.payload.list)
                    oldList.platforms = this.getListPlatforms(oldList)

                    if (this.currentList?.id === currentList.id) {
                        this.currentList = oldList
                    }

                    this.onListModalClose()
                    this.$successHandler('Successfully updated list.')
                } catch (error) {
                    this.$errorHandler(error, {
                        title: 'Updating List',
                        message: 'Error updating this list.',
                    })
                } finally {
                    this.modal.isSubmitting = false
                }

                return
            }

            if (type === 'copy') {
                trackEvent(AnalyticalEvents.ListCreated, {
                    list_name: trimmedName,
                    source: 'copy',
                    source_list_id: currentList.id,
                })

                try {
                    const { data } = await this.$axios.post(`/lists/${currentList.id}/copy`, {
                        list: {
                            name: trimmedName,
                        },
                    })
                    const copiedList = this.prepareOwnedList(data.payload.list)

                    this.lists.user.push(copiedList)
                    this.state.listType = 'user'
                    this.onListSelect(copiedList)
                    await this.getLists()
                    this.onListModalClose()
                    this.$successHandler('Successfully copied list.')
                } catch (error) {
                    this.$errorHandler(error, {
                        title: 'Copying List',
                        message: 'Error copying this list.',
                    })
                } finally {
                    this.modal.isSubmitting = false
                }

                return
            }

            if (type === 'delete') {
                try {
                    await this.$axios.delete(`/lists/${currentList.id}`)

                    const wasCurrentList = this.currentList?.id === currentList.id
                    this.lists.user = _.filter(this.lists.user, (item) => item.id !== currentList.id)

                    if (wasCurrentList) {
                        const firstList = _.first([...this.lists.user, ...this.lists.shared])

                        if (!firstList) {
                            this.currentList = null
                            this.creators = []
                        } else {
                            this.onListSelect(firstList)
                        }
                    }

                    this.onListModalClose()
                    this.$successHandler('Successfully deleted list.')
                } catch (error) {
                    this.$errorHandler(error, {
                        title: 'Deleting List',
                        message: 'Error deleting this list.',
                    })
                } finally {
                    this.modal.isSubmitting = false
                }
            }
        },

        onCreatorsEdit({ type, creators, campaignId, supercampaignId }) {
            if (type === 'remove-selected') {
                const body = {
                    creators,
                }

                this.$axios
                    .delete(`/lists/${this.currentList.id}/creators`, {
                        data: body,
                    })
                    .then(({ data }) => {
                        const removedCreatorsLookup = _.keyBy(creators, 'platform_user_id')

                        this.creators = _.filter(
                            this.creators,
                            (creator) => !removedCreatorsLookup[creator.list_creator.platform_user_id],
                        )

                        this.getLists()

                        this.$successHandler(`Successfully removed ${data.payload.count} creators from list.`)
                    })
                    .catch((error) => {
                        this.$errorHandler(error, {
                            title: 'Removing Creators',
                            message: 'Error removing creators from list.',
                        })
                    })
            }

            if (type === 'add-selected') {
                const body = {
                    creators,
                }

                this.$axios
                    .post(`/supercampaigns/${supercampaignId}/campaigns/${campaignId}/participants`, body)
                    .then(() => {
                        this.$successHandler('Successfully added creators.')
                        this.getLists()
                    })
                    .catch((error) => {
                        this.$errorHandler(error, {
                            title: 'Adding Creators',
                            message: 'Error adding creators to list.',
                        })
                    })
            }

            if (type === 'add-all') {
                const body = {
                    creators,
                }

                this.$axios
                    .post(`/supercampaigns/${supercampaignId}/campaigns/${campaignId}/participants`, body)
                    .then(() => {
                        this.$successHandler('Successfully added all creators.')
                        this.getLists()
                    })
                    .catch((error) => {
                        this.$errorHandler(error, {
                            title: 'Adding Creators',
                            message: 'Error adding all creators to campaign.',
                        })
                    })
            }
        },

        onListOption(type) {
            this.lists.filter.type = type
            this.lists.filter.search = ''
        },

        async onAddCreators({ creators }) {
            this.state.creators.isAdding = true

            const body = {
                creators,
            }

            // Start the background action
            const actionId = await this.addBackgroundAction({
                description: `Adding ${creators.length} creators to ${this.currentList.name}`,
                estimatedDuration: creators.length * 10_000,
            })

            this.$axios
                .post(`/lists/${this.currentList.id}/creators`, body)
                .then(({ data }) => {
                    const newCreators = _.flatten(_.map(data.payload.results, 'new_creators'))

                    if (newCreators?.length > 0) {
                        this.creators.push(...newCreators)

                        // Update and complete the background action
                        this.updateBackgroundAction(actionId, {
                            description: `Added ${newCreators.length} creators to list "${this.currentList.name}"`,
                        })

                        this.getLists()
                    } else {
                        // Update and complete the background action
                        this.updateBackgroundAction(actionId, {
                            description: `No new creators were added to list "${this.currentList.name}" as they were either duplicates or there was an issue with their profile.`,
                        })
                    }

                    this.completeBackgroundAction(actionId)

                    // this.$successHandler(`Successfully added ${newCreatorsCount} creators to list!`)

                    this.state.creators.isAdding = false
                })
                .catch(() => {
                    this.state.creators.isAdding = false

                    // Update the background action to show the error
                    this.updateBackgroundAction(actionId, {
                        description: `Error adding creators to ${this.currentList.name}`,
                    })
                    this.completeBackgroundAction(actionId)

                    // this.$errorHandler(error, {
                    //     title: `Adding Creators`,
                    //     message:
                    //         error.response?.status === 400
                    //             ? error.response.data.message
                    //             : 'Error adding creators to list.',
                    // })
                })
        },

        onBulkImportCreators() {
            this.getLists()
            // handle success but some creators may not have been added
            this.$successHandler('Successfully bulk imported creators to list.')
        },

        onFetchSharedTo() {
            if (!this.currentList) {
                return null
            }

            if (this.currentList.sharedTo) {
                return null
            }

            return this.$axios
                .get(`/lists/${this.currentList.id}/share`)
                .then(({ data }) => {
                    this.currentList.sharedTo = {
                        organizations: data.payload.organizations,
                        operators: data.payload.operators,
                    }
                })
                .catch(() => {})
        },

        checkScrollbar() {
            const el = this.$refs.scrollContainer
            if (el) {
                this.hasScrollbar = el.scrollHeight > el.clientHeight
            }
        },
    },
}
</script>
